import { JsonPipe } from '@angular/common';
import { Component, HostBinding, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ElementHostComponent } from '@components/element-widgets/element-host/element-host.component';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { LOG } from 'src/app/common/util/log';
import { BusinessObjectsElementService, DocumentElementResponse } from 'src/openapi-client';
import { ApiDebugService } from '../api-debug.service';
import { JsonComponent } from '../json/json.component';
import { visualizeSpacePipe } from '../visualize-space.pipe';

interface Report {
  index: number;
  name: string;
  id: string;
  elements: DocumentElementResponse[];
}

@Component({
  selector: 'debug-report-info',
  standalone: true,
  imports: [FormsModule, JsonComponent, JsonPipe, visualizeSpacePipe, ElementHostComponent],
  providers: [ApiDebugService],
  templateUrl: './report-info.component.html',
  styleUrl: './report-info.component.scss',
})
export class DebugReportInfoComponent implements OnInit, OnDestroy {
  protected documentId: string = '';
  protected funds: string = '';

  protected reports: Report[] = [];

  protected prompts: Record<string, unknown> = undefined;
  protected inputControls: Record<string, unknown> = undefined;

  protected path: string = '';

  protected loading: boolean = false;

  private readonly elementsService = inject(BusinessObjectsElementService);
  private readonly debugService = inject(ApiDebugService);
  private readonly zone = inject(NgZone);

  @HostBinding('attr.hidden') protected hidden: true | null = true;

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      document.addEventListener('keyup', this.handleKeys);
    });
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      document.removeEventListener('keyup', this.handleKeys);
    });
  }

  protected async clearCache() {
    this.loading = true;
    try {
      await firstValueFrom(this.debugService.clearCache());
    } finally {
      this.loading = false;
    }
  }

  protected async fetch(): Promise<void> {
    if (this.loading) {
      return;
    }

    if (!this.documentId || this.documentId.length < 1) {
      return;
    }

    this.loading = true;
    this.path = '';
    this.prompts = undefined;
    this.inputControls = undefined;

    try {
      const requests: Promise<DocumentElementResponse[]>[] = [];

      const meta: Record<string, unknown> = await firstValueFrom(this.debugService.metadata(this.documentId));

      this.prompts = meta['prompts'] ? (meta['prompts'] as any) : undefined;
      this.inputControls = meta['inputControls'] ? (meta['inputControls'] as any) : undefined;

      const reports: unknown[] = meta['reports'] as unknown[];
      const reportCount: number = reports.length;

      for (let reportIndex: number = 0; reportIndex < reportCount; reportIndex++) {
        requests.push(
          firstValueFrom(
            this.elementsService.getElements(this.documentId, reportIndex, this.funds || undefined).pipe(
              map(result => result.elements),
              catchError(err => {
                LOG.error(err);
                return of(undefined);
              }),
            )
          )
        );
      }

      const results: DocumentElementResponse[][] = [];
      for (const request of requests) {
        const result: DocumentElementResponse[] = await request;
        results.push(result);
      }

      this.reports = results.map<Report>((result, reportIndex) => {
        return <Report>{
          index: reportIndex,
          name: reports[reportIndex]['name'],
          id: reports[reportIndex]['id'],
          elements: result,
        };
      });

      this.path = await firstValueFrom(this.debugService.getPath(this.documentId));
    } finally {
      this.loading = false;
    }
  }

  private readonly handleKeys = (event: KeyboardEvent) => {
    if (event.ctrlKey && event.key == 'm') {
      this.zone.run(() => {
        this.hidden = this.hidden === true ? null : true;
      });
    }
  };
}
